import { useEffect, useState, useCallback, useRef } from 'react';
import adminAxios from '../lib/adminAxios';
import { Loader2, RefreshCw, Mail, MailOpen, Check, ChevronDown, ChevronUp, Sparkles, Copy, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CFG = {
  new:      { label: 'New',      color: 'text-gold',       dot: 'bg-gold' },
  read:     { label: 'Read',     color: 'text-mb-muted',   dot: 'bg-mb-muted' },
  replied:  { label: 'Replied',  color: 'text-green-400',  dot: 'bg-green-400' },
  archived: { label: 'Archived', color: 'text-mb-muted/50', dot: 'bg-mb-muted/40' },
};

// ── Typing animation helper ───────────────────────────────────
function typeText(fullText, setter, onDone, speed = 9) {
  let i = 0;
  setter('');
  const id = setInterval(() => {
    i++;
    setter(fullText.slice(0, i));
    if (i >= fullText.length) { clearInterval(id); onDone?.(); }
  }, speed);
  return () => clearInterval(id); // cleanup
}

// ── Single enquiry row ─────────────────────────────────────────
function EnquiryRow({ enquiry, onStatusChange }) {
  const [expanded, setExpanded]   = useState(false);
  const [draft, setDraft]         = useState('');
  const [streaming, setStreaming] = useState(false);
  const [copied, setCopied]       = useState(false);
  const [streamErr, setStreamErr] = useState('');

  const markRead = useCallback(async () => {
    if (enquiry.status !== 'new') return;
    try {
      await adminAxios.patch(`${API}/admin/enquiries/${enquiry.id}/status`, { status: 'read' });
      onStatusChange(enquiry.id, 'read');
    } catch { /* silent */ }
  }, [enquiry.id, enquiry.status, onStatusChange]);

  const handleExpand = () => {
    setExpanded((v) => !v);
    if (!expanded) markRead();
  };

  const stopTypingRef = useRef(null);

  const generateReply = async () => {
    setDraft('');
    setStreamErr('');
    setStreaming(true);
    if (stopTypingRef.current) stopTypingRef.current();
    try {
      const { data } = await adminAxios.post(`${API}/admin/ai/draft-reply`, {
        contact:      enquiry.contact,
        company:      enquiry.company,
        set_interest: enquiry.set_interest,
        quantity:     enquiry.quantity,
        message:      enquiry.message,
      });
      stopTypingRef.current = typeText(data.text, setDraft, () => setStreaming(false));
    } catch (e) {
      setStreamErr(e?.response?.data?.detail || e.message);
      setStreaming(false);
    }
  };

  const copyDraft = () => {
    navigator.clipboard.writeText(draft);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const markReplied = async () => {
    try {
      await adminAxios.patch(`${API}/admin/enquiries/${enquiry.id}/status`, { status: 'replied' });
      onStatusChange(enquiry.id, 'replied');
    } catch { /* silent */ }
  };

  const cfg = STATUS_CFG[enquiry.status] || STATUS_CFG.new;

  return (
    <div
      className={`hairline-b border-subtle transition-colors ${enquiry.status === 'archived' ? 'opacity-40' : ''}`}
      data-testid={`enquiry-row-${enquiry.id}`}
    >
      {/* Summary */}
      <div
        className="grid grid-cols-[16px_1fr_140px_90px_100px_24px] gap-3 items-center px-4 py-3 cursor-pointer hover:bg-mb-surface/40"
        onClick={handleExpand}
        data-testid={`enquiry-toggle-${enquiry.id}`}
      >
        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
        <div>
          <div className="font-display text-[13px] text-mb">
            {enquiry.contact}
            <span className="text-mb-muted mx-1.5">·</span>
            <span className="text-mb-muted">{enquiry.company}</span>
          </div>
          {enquiry.set_interest && (
            <div className="eyebrow-sm text-mb-muted mt-0.5">{enquiry.set_interest}</div>
          )}
        </div>
        <div className="eyebrow-sm text-mb-muted truncate hidden md:block">{enquiry.email}</div>
        <div className={`eyebrow-sm ${cfg.color}`}>{cfg.label}</div>
        <div className="eyebrow-sm text-mb-muted text-right">
          {new Date(enquiry.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
        </div>
        {expanded ? <ChevronUp size={13} className="text-mb-muted" /> : <ChevronDown size={13} className="text-mb-muted" />}
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-4 pb-6 pt-1 bg-mb-surface/20">
          {/* Enquiry details */}
          <div className="grid md:grid-cols-2 gap-4 mb-5">
            <div>
              {enquiry.message && (
                <div className="mb-4">
                  <div className="eyebrow-sm text-mb-muted mb-1.5">Message</div>
                  <p className="text-[13px] text-mb leading-relaxed whitespace-pre-wrap">{enquiry.message}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                {enquiry.quantity && (
                  <div>
                    <div className="eyebrow-sm text-mb-muted mb-0.5">Quantity</div>
                    <div className="text-[13px] text-mb">{enquiry.quantity}</div>
                  </div>
                )}
                <div>
                  <div className="eyebrow-sm text-mb-muted mb-0.5">Email</div>
                  <a
                    href={`mailto:${enquiry.email}`}
                    className="text-[13px] text-gold hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {enquiry.email}
                  </a>
                </div>
              </div>
            </div>

            {/* Status actions */}
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap gap-2">
                {Object.entries(STATUS_CFG).map(([s, c]) => (
                  s !== enquiry.status && (
                    <button
                      key={s}
                      className="eyebrow-sm px-3 py-1.5 hairline border-subtle hover:border-gold/60 transition-colors text-mb-muted hover:text-mb"
                      onClick={() => adminAxios.patch(`${API}/admin/enquiries/${enquiry.id}/status`, { status: s }).then(() => onStatusChange(enquiry.id, s))}
                      data-testid={`enquiry-mark-${s}-${enquiry.id}`}
                    >
                      Mark {c.label}
                    </button>
                  )
                ))}
              </div>
            </div>
          </div>

          {/* AI Reply Drafter */}
          <div className="hairline border-subtle p-4 bg-mb-surface/50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles size={13} className="text-gold" />
                <span className="eyebrow-sm text-gold">Claude reply draft</span>
              </div>
              <div className="flex items-center gap-2">
                {draft && !streaming && (
                  <>
                    <button
                      className="eyebrow-sm px-2 py-1 hairline border-subtle hover:border-gold/60 transition-colors text-mb-muted hover:text-mb flex items-center gap-1.5"
                      onClick={copyDraft}
                      data-testid={`enquiry-copy-draft-${enquiry.id}`}
                    >
                      {copied ? <Check size={11} className="text-gold" /> : <Copy size={11} />}
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                    <button
                      className="eyebrow-sm px-2 py-1 hairline border-subtle border-green-400/30 hover:border-green-400/60 text-green-400 transition-colors flex items-center gap-1.5"
                      onClick={markReplied}
                      data-testid={`enquiry-mark-replied-${enquiry.id}`}
                    >
                      <Check size={11} />Mark replied
                    </button>
                  </>
                )}
                <button
                  className="btn-gold text-[10px] px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-50"
                  onClick={generateReply}
                  disabled={streaming}
                  data-testid={`enquiry-generate-reply-${enquiry.id}`}
                >
                  {streaming
                    ? <><Loader2 size={11} className="animate-spin" />Writing…</>
                    : <><Sparkles size={11} />{draft ? 'Regenerate' : 'Draft reply'}</>}
                </button>
              </div>
            </div>

            {streamErr && (
              <div className="flex items-center gap-2 text-red-400 eyebrow-sm mb-2">
                <AlertCircle size={11} /> {streamErr}
              </div>
            )}

            {(draft || streaming) && (
              <textarea
                className="mb-input w-full text-[13px] leading-relaxed"
                rows={6}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={streaming ? '— writing —' : ''}
                data-testid={`enquiry-draft-${enquiry.id}`}
              />
            )}

            {!draft && !streaming && (
              <p className="text-[11px] text-mb-muted italic">
                Click "Draft reply" and Claude will write a personalised reply in the house tone.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const FILTERS = [
  { value: 'all',      label: 'All' },
  { value: 'new',      label: 'New' },
  { value: 'read',     label: 'Read' },
  { value: 'replied',  label: 'Replied' },
  { value: 'archived', label: 'Archived' },
];

export default function EnquiriesPanel() {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [filter, setFilter]       = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await adminAxios.get(`${API}/admin/enquiries`);
      setEnquiries(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = useCallback((id, status) => {
    setEnquiries((prev) =>
      prev.map((e) => (e.id === id ? { ...e, status } : e))
    );
  }, []);

  const filtered = filter === 'all' ? enquiries : enquiries.filter((e) => e.status === filter);
  const newCount = enquiries.filter((e) => e.status === 'new').length;

  return (
    <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24" data-testid="admin-enquiries-panel">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="eyebrow text-gold mb-4 flex items-center gap-3">
            Enquiries
            {newCount > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gold text-black text-[10px] font-bold">
                {newCount}
              </span>
            )}
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-mb">Inbox · enquiries &amp; replies.</h2>
        </div>
        <button
          className="btn-ghost flex items-center gap-2 text-sm"
          onClick={load}
          disabled={loading}
          data-testid="enquiries-refresh"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {FILTERS.map(({ value, label }) => (
          <button
            key={value}
            className={`eyebrow-sm px-3 py-1.5 hairline border-subtle transition-colors ${
              filter === value ? 'bg-gold text-black border-gold' : 'text-mb-muted hover:text-mb'
            }`}
            onClick={() => setFilter(value)}
            data-testid={`enquiries-filter-${value}`}
          >
            {label}
            {value === 'new' && newCount > 0 && ` (${newCount})`}
          </button>
        ))}
      </div>

      <div className="bg-mb-surface hairline border-subtle">
        {/* Header */}
        <div className="grid grid-cols-[16px_1fr_140px_90px_100px_24px] gap-3 items-center px-4 py-2 hairline-b border-subtle eyebrow-sm text-mb-muted">
          <div />
          <div>Contact · Company</div>
          <div className="hidden md:block">Email</div>
          <div>Status</div>
          <div className="text-right">Date</div>
          <div />
        </div>

        {loading && (
          <div className="flex items-center gap-2 px-4 py-6 text-mb-muted">
            <Loader2 size={14} className="animate-spin" />
            <span className="eyebrow-sm">Loading enquiries…</span>
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 px-4 py-4 text-red-400">
            <AlertCircle size={14} />
            <span className="eyebrow-sm">{error}</span>
          </div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div className="px-4 py-10 text-center">
            <Mail size={24} className="text-mb-muted mx-auto mb-3" strokeWidth={1} />
            <p className="eyebrow-sm text-mb-muted">
              {filter === 'all' ? 'No enquiries yet.' : `No ${filter} enquiries.`}
            </p>
          </div>
        )}
        {!loading && filtered.map((enquiry) => (
          <EnquiryRow
            key={enquiry.id}
            enquiry={enquiry}
            onStatusChange={handleStatusChange}
          />
        ))}
      </div>

      {filtered.length > 0 && (
        <div className="mt-4 eyebrow-sm text-mb-muted">
          Showing {filtered.length} of {enquiries.length} enquiries · Click any row to expand
        </div>
      )}
    </section>
  );
}
